import React, { useState, useEffect } from 'react';
import { NavBar } from "./components/common/NavBar";
import { Header } from "./components/common/Header";
import * as api from "./api/user-app-api.js";

// Screens
import { Dashboard } from "./components/screens/Dashboard";
import { CartScreen } from "./components/screens/CartScreen";
import { OrderSummaryScreen } from "./components/screens/OrderSummaryScreen";
import { MyAddressScreen } from "./components/screens/MyAddressScreen";
import { OrderHistoryScreen } from "./components/screens/OrderHistoryScreen";
import { OrderDetailScreen } from "./components/screens/OrderDetailScreen";
import { ProfileScreen } from "./components/screens/ProfileScreen";
import { HelpScreen } from "./components/screens/HelpScreen";
import { ContactScreen } from "./components/screens/ContactScreen";
import { PrivacyPolicyScreen } from "./components/screens/PrivacyPolicyScreen";
import { AuthScreen } from './components/screens/AuthScreen';
import { Translator } from "./components/common/Translator";

import { fetchUserOrders } from './api/user-app-api';
import { INITIAL_ADDRESSES } from './utils/constants';
import { SplashScreen } from './components/screens/SplashScreen';

export default function App() {
    const [showSplash, setShowSplash] = useState(true);

    const [view, setViewInternal] = useState("login");
    const [viewParams, setViewParams] = useState({});
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [cart, setCart] = useState([]);
    const [addresses, setAddresses] = useState(INITIAL_ADDRESSES);
    const [orders, setOrders] = useState([]);
    const [logo, setLogo] = useState(null);
    const [logoLoading, setLogoLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const fetchLogo = async () => {
            try {
                const res = await api.fetchSettings();

                if (isMounted && res?.data?.logo_url) {
                    setLogo(res.data.logo_url);
                }
            } catch (e) {
                console.error("Failed to fetch logo:", e);
            } finally {
                if (isMounted) setLogoLoading(false);
            }
        };

        fetchLogo();

        return () => {
            isMounted = false;
        };
    }, []);


    const setView = (newView, params = {}) => {
        setViewParams(params);
        setViewInternal(newView);
    };

    // ======================================================
    // ✅ SPLASH SCREEN (2 SECONDS)
    // ======================================================
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowSplash(false);
        }, 2000);

        return () => clearTimeout(timer);
    }, []);

    // ======================================================
    // ✅ AUTO-LOGIN IF TOKEN EXISTS
    // ======================================================
    useEffect(() => {
        if (showSplash) return;

        const autoLogin = async () => {
            const token = localStorage.getItem('authToken');
            const storedUserId = localStorage.getItem('userId');
            const isLogged = localStorage.getItem('isLoggedIn') === 'true';

            if (storedUserId && token && isLogged) {
                setIsLoggedIn(true);
                setUserId(storedUserId);
                setView('dashboard');
                return;
            }

            setIsLoggedIn(false);
            setView('login');
        };

        autoLogin();
    }, [showSplash]);

    // ======================================================
    // ✅ FETCH USER ORDERS AFTER LOGIN
    // ======================================================
    useEffect(() => {
        if (isLoggedIn) {
            (async () => {
                try {
                    const res = await fetchUserOrders();
                    if (Array.isArray(res.data)) setOrders(res.data);
                } catch (err) {
                    console.error("Failed to fetch orders:", err);
                }
            })();
        }
    }, [isLoggedIn]);


    useEffect(() => {
    const interval = setInterval(() => {
        const googleEl = document.getElementById("google_translate_element");
        const headerEl = document.getElementById("google_translate_container");

        if (googleEl && headerEl && googleEl.childNodes.length > 0) {
            headerEl.innerHTML = "";
            headerEl.appendChild(googleEl);
            clearInterval(interval);
        }
    }, 500);

    return () => clearInterval(interval);
}, []);


    // ======================================================
    // ✅ SPLASH SHORT-CIRCUIT
    // ======================================================
    if (showSplash) {
        return <SplashScreen />;
    }

    // ======================================================
    // ✅ ROUTING
    // ======================================================
    let content = null;
    switch (view) {
        case "login":
            content = (
                <AuthScreen
                    setView={setView}
                    setUserId={setUserId}
                    setIsLoggedIn={setIsLoggedIn}
                    setUser={setUser}
                />
            );
            break;
        case "dashboard":
            content = <Dashboard setView={setView} />;
            break;
        case "cart":
            content = <CartScreen cart={cart} setCart={setCart} setView={setView} params={viewParams} />;
            break;
        case "orderSummary":
            content = (
                <OrderSummaryScreen
                    params={viewParams}
                    setView={setView}
                    addresses={addresses}
                    userId={userId}
                    setCart={setCart}
                />
            );
            break;
        case "myAddress":
            content = (
                <MyAddressScreen
                    addresses={addresses}
                    setAddresses={setAddresses}
                    setView={setView}
                    userId={userId}
                    params={viewParams}   // 👈 ADD THIS
                />
            );
            break;
        case "orderHistory":
            content = <OrderHistoryScreen orders={orders} setView={setView} />;
            break;
        case "orderDetail":
            content = <OrderDetailScreen params={viewParams} setView={setView} />;
            break;
        case "profile":
            content = (
                <ProfileScreen
                    user={user}
                    setView={setView}
                    setIsLoggedIn={setIsLoggedIn}
                    setUser={setUser}
                />
            );
            break;
        case "help":
            content = <HelpScreen setView={setView} />;
            break;
        case "contact":
            content = <ContactScreen setView={setView} />;
            break;
        case "privacyPolicy":
            content = <PrivacyPolicyScreen setView={setView} />;
            break;
        default:
            content = <Dashboard setView={setView} />;
    }

    // ======================================================
    // ✅ HEADER LOGIC
    // ======================================================
    const headerTitleMap = {
        dashboard: "Dashboard",
        cart: "My Cart",
        orderSummary: "Order Summary",
        myAddress: "My Addresses",
        orderHistory: "Order History",
        orderDetail: "Order Details",
        profile: "My Profile",
        help: "Help & Support",
        contact: "Contact Us",
        privacyPolicy: "Privacy Policy",
    };

    const headerTitle = headerTitleMap[view] || "Dashboard";

    const onBack = () => {
        if (view === "dashboard" || view === "login") return;
        setView("dashboard");
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
                body { font-family: 'Inter', sans-serif; }
                ::-webkit-scrollbar { width: 8px; }
                ::-webkit-scrollbar-track { background: #f0fff0; border-radius: 10px; }
                ::-webkit-scrollbar-thumb { background: #01a499; border-radius: 10px; }
                ::-webkit-scrollbar-thumb:hover { background: #01857c; }
            `}</style>

            {view !== 'login' && (
                <Header title={headerTitle} onBack={onBack} rightContent={
                     <img
                         src={logo}
                         alt="OzNULI"
                         style={{ width: "5rem" }}
                         className="object-contain"
                     />
                    // <Translator />
                } />
            )}

            {content}

            {isLoggedIn && view !== "login" && (
                <NavBar
                    currentView={view}
                    setView={setView}
                    cartItemCount={cart.length}
                />
            )}
        </div>
    );
}
